/* eslint-disable */
/**
 * Inspector — embedded Brief + Styles panel for UX wizards.
 * Modes: off | brief | styles
 */
import { createContext, useContext, useMemo, useState } from "react";
import { T, useTheme } from "../tokens";
import {
  enrichTokensForExport,
  formatBriefForLlm,
  formatStylesCopyBlock,
} from "../tokenExportMap";

const InspectorCtx = createContext({
  mode: "off",
  setMode: () => {},
  selection: null,
  select: () => {},
  clear: () => {},
});

export function InspectProvider({ initialMode = "off", children }) {
  const [mode, setMode] = useState(initialMode === "spec" ? "brief" : initialMode);
  const [selection, setSelection] = useState(null);
  const value = useMemo(
    () => ({
      mode,
      setMode: (next) => setMode(next === "spec" ? "brief" : next),
      selection,
      select: (meta) => setSelection(meta),
      clear: () => setSelection(null),
    }),
    [mode, selection]
  );
  return <InspectorCtx.Provider value={value}>{children}</InspectorCtx.Provider>;
}

export function useInspector() {
  return useContext(InspectorCtx);
}

export function Inspect({ id, label, spec, tokens, css, note, children }) {
  const { mode, selection, select } = useInspector();
  if (mode !== "styles") return children;

  const isSelected = selection?.id === id;
  const outline = isSelected ? `2px solid ${T.brand}` : `1px dashed ${T.brand}55`;
  const bg = isSelected ? T.brandSoft : "transparent";

  return (
    <div
      onClick={(event) => {
        event.stopPropagation();
        select({ id, label: label || id, spec, tokens, css, note });
      }}
      style={{
        outline,
        outlineOffset: 2,
        background: bg,
        borderRadius: 4,
        cursor: "pointer",
        transition: `outline ${T.fast} ${T.ease}, background ${T.fast} ${T.ease}`,
      }}
    >
      {children}
    </div>
  );
}

export function InspectorToolbar({ dark, style = {} }) {
  const th = useTheme(dark);
  const { mode, setMode, clear } = useInspector();
  const opts = [
    { id: "off", label: "Off" },
    { id: "brief", label: "Brief" },
    { id: "styles", label: "Styles" },
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 0,
        background: th.muted,
        border: `1px solid ${th.border}`,
        borderRadius: 18,
        padding: 2,
        ...style,
      }}
    >
      {opts.map((option) => {
        const active = mode === option.id;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => {
              setMode(option.id);
              if (option.id === "off") clear();
            }}
            style={{
              background: active ? th.surface : "transparent",
              border: "none",
              borderRadius: 14,
              padding: "4px 12px",
              fontSize: 11,
              fontWeight: 600,
              color: active ? T.brand : th.textSec,
              cursor: "pointer",
              boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              transition: th.tr,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function copyText(text) {
  try {
    navigator.clipboard?.writeText(String(text));
  } catch {
    /* ignore */
  }
}

function CopyRow({ keyName, value, th, mono = true }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 0",
        borderBottom: `1px solid ${th.border}`,
      }}
    >
      <span style={{ fontSize: 11, color: th.textSec, minWidth: 110 }}>{keyName}</span>
      <code
        style={{
          flex: 1,
          fontFamily: mono ? "monospace" : "inherit",
          fontSize: 11,
          color: th.text,
          background: th.muted,
          padding: "2px 6px",
          borderRadius: 4,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {typeof value === "object" ? JSON.stringify(value) : String(value)}
      </code>
      <button
        type="button"
        onClick={() => copyText(value)}
        title="Copy"
        style={{
          background: "none",
          border: `1px solid ${th.border}`,
          borderRadius: 4,
          padding: "2px 6px",
          fontSize: 10,
          color: th.textSec,
          cursor: "pointer",
        }}
      >
        ⧉
      </button>
    </div>
  );
}

function SectionTitle({ children, th }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: th.textMuted,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

function Empty({ text, th }) {
  return (
    <div
      style={{
        padding: 16,
        textAlign: "center",
        background: th.muted,
        borderRadius: T.rmd,
        color: th.textMuted,
        fontSize: 11,
      }}
    >
      {text}
    </div>
  );
}

const BRIEF_FIELD_ORDER = [
  "route",
  "shell",
  "auth",
  "app",
  "primaryAction",
  "states",
  "mobile",
  "tierGating",
  "edgeCases",
];

function BriefView({ flowContext, th }) {
  if (!flowContext) {
    return <Empty th={th} text="Open a flow step to see the dev brief." />;
  }

  const { flowLabel, stepLabel, stepNote, shell, shellBlurb, devBrief = {} } = flowContext;
  const markdown = formatBriefForLlm(flowContext);

  return (
    <div>
      <SectionTitle th={th}>Step notes</SectionTitle>
      <div
        style={{
          fontSize: 11,
          color: th.textSec,
          lineHeight: 1.65,
          marginBottom: 12,
          padding: "10px 12px",
          background: th.muted,
          borderRadius: T.rmd,
          border: `1px solid ${th.border}`,
        }}
      >
        {stepNote || "(none)"}
      </div>

      <SectionTitle th={th}>Shell</SectionTitle>
      <div style={{ fontFamily: "monospace", fontSize: 10, color: T.cyan, marginBottom: 4 }}>{shell}</div>
      <div style={{ fontSize: 10, color: th.textSec, lineHeight: 1.5, marginBottom: 12 }}>{shellBlurb}</div>

      <SectionTitle th={th}>Build checklist</SectionTitle>
      {BRIEF_FIELD_ORDER.map((field) => {
        const value = devBrief[field];
        if (value === undefined || value === null || value === "") return null;
        return <CopyRow key={field} keyName={field} value={value} th={th} mono={field === "states" || field === "edgeCases"} />;
      })}

      <button
        type="button"
        onClick={() => copyText(markdown)}
        style={{
          marginTop: 12,
          width: "100%",
          background: T.brand,
          color: "#fff",
          border: "none",
          borderRadius: T.rsm,
          padding: "8px 12px",
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Copy for LLM
      </button>
    </div>
  );
}

function StylesView({ selection, th, dark }) {
  const rows = enrichTokensForExport(selection?.tokens || {}, selection?.css || {}, dark);

  return (
    <div>
      <SectionTitle th={th}>Styles</SectionTitle>
      {rows.length === 0 ? (
        <Empty th={th} text="Click a highlighted region to inspect styles." />
      ) : (
        <>
          <div style={{ fontSize: 10, color: th.textMuted, marginBottom: 6 }}>
            CSS variable + token intent (theme: {dark ? "dark" : "light"})
          </div>
          {rows.map((row) => {
            const copyLine = row.cssVar
              ? `${camelToKebab(row.prop)}: var(${row.cssVar}); /* ${row.token} */`
              : `${camelToKebab(row.prop)}: ${row.resolved}; /* ${row.token} */`;
            return (
              <div key={row.prop} style={{ marginBottom: 10 }}>
                <CopyRow keyName={row.prop} value={copyLine} th={th} />
                <div style={{ fontSize: 9, color: th.textMuted, paddingLeft: 118 }}>
                  resolved: {row.resolved}
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => copyText(formatStylesCopyBlock(rows, dark))}
            style={{
              marginTop: 6,
              width: "100%",
              background: T.brand,
              color: "#fff",
              border: "none",
              borderRadius: T.rsm,
              padding: "8px 12px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Copy all styles
          </button>
        </>
      )}
    </div>
  );
}

function camelToKebab(propertyName) {
  return propertyName.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}

/**
 * @param {object} props
 * @param {boolean} props.dark
 * @param {number} [props.width]
 * @param {boolean} [props.embedded]
 * @param {() => void} [props.onClose]
 * @param {object} [props.flowContext]
 */
export function InspectorPanel({
  dark,
  width = 300,
  embedded = false,
  onClose,
  flowContext = null,
}) {
  const th = useTheme(dark);
  const { mode, selection, clear, setMode } = useInspector();

  const handleClose = () => {
    clear();
    setMode("off");
    onClose?.();
  };

  const panelTitle =
    mode === "brief"
      ? flowContext?.stepLabel || "Dev brief"
      : selection?.label || "Styles";

  const panelStyle = embedded
    ? {
        position: "relative",
        flexShrink: 0,
        width,
        height: "100%",
        background: th.surface,
        borderLeft: `1px solid ${th.border}`,
        padding: 16,
        overflowY: "auto",
      }
    : {
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width,
        background: th.surface,
        borderLeft: `1px solid ${th.border}`,
        padding: 16,
        overflowY: "auto",
        boxShadow: dark ? "none" : "-4px 0 16px rgba(0,0,0,0.06)",
        zIndex: 50,
        transition: th.tr,
      };

  return (
    <div style={panelStyle}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: th.text, flex: 1, minWidth: 0 }}>
          {panelTitle}
        </div>
        {mode === "styles" && selection && (
          <button
            type="button"
            onClick={clear}
            title="Clear selection"
            style={{
              background: "none",
              border: `1px solid ${th.border}`,
              borderRadius: T.rsm,
              padding: "2px 8px",
              fontSize: 10,
              color: th.textSec,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={handleClose}
          title="Close panel"
          aria-label="Close panel"
          style={{
            background: "none",
            border: `1px solid ${th.border}`,
            borderRadius: T.rsm,
            padding: "2px 8px",
            fontSize: 14,
            lineHeight: 1,
            color: th.textSec,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>

      {mode === "brief" ? (
        <BriefView flowContext={flowContext} th={th} />
      ) : mode === "styles" ? (
        <StylesView selection={selection} th={th} dark={dark} />
      ) : null}
    </div>
  );
}
